import '@/shared/tracking/events.quiz-share-guest';
import '@/shared/tracking/events.quiz-trial-gate-copy';

import { trackEvent } from '@zep/logger/tracking';

import { type QuizCopyContext } from '@/shared/tracking/quizCopySource';

export type QuizCopyResult =
  | { status: 'success'; copiedQuizsetId: string }
  | { status: 'fail'; error?: unknown };

/** 실패 사유는 이름만 싣는다 — 메시지에는 사용자 입력이 섞일 수 있다. */
const toReason = (error: unknown) =>
  error instanceof Error ? error.name : 'unknown';

/**
 * 복사 전환 퍼널의 **마지막 칸**.
 *
 * 예전에는 빌드 페이지 저장 핸들러(`BuildHeaderSubmitButton`)가 이 두 이벤트를 쏘았다. 즉시
 * 복사로 옮기면서 그 집이 사라졌는데, 복사가 일어나는 자리가 **둘**이 됐다 — 로그인 사용자의
 * 제자리 복사(원탭)와 인증을 마친 뒤의 착지 경로. 두 곳이 같은 말을 하도록 여기 모은다.
 *
 * **이름과 prop 은 옛것 그대로다.** 경로가 바뀌었다고 이름을 바꾸면 클릭→완료 전환율의 시계열이
 * 끊긴다. 두 이벤트가 `quiz_set_id` / `origin_quiz_set_id` 로 키 이름이 다른 것도 그대로 둔다.
 *
 * 출처(`copySource`)가 없으면 아무것도 보내지 않는다 — 튜토리얼 헤더·젤리처럼 퍼널에 속하지
 * 않는 복사까지 세면 전환율 분모가 오염된다.
 */
export const trackQuizCopyComplete = (
  context: QuizCopyContext,
  sourceQuizsetId: string,
  result: QuizCopyResult,
) => {
  if (context.copySource === 'guest_share' && context.shareFlowId) {
    trackEvent('quiz_share_guest_copy_complete', {
      share_flow_id: context.shareFlowId,
      origin_quiz_set_id: sourceQuizsetId,
      ...(result.status === 'success'
        ? { result: 'success', copied_quiz_set_id: result.copiedQuizsetId }
        : { result: 'fail', reason: toReason(result.error) }),
    });
  }

  if (context.copySource === 'trial_gate' && context.trialSessionId) {
    trackEvent('quiz_play_trial_gate_copy_complete', {
      trial_session_id: context.trialSessionId,
      quiz_set_id: sourceQuizsetId,
      ...(result.status === 'success'
        ? { result: 'success', copied_quiz_set_id: result.copiedQuizsetId }
        : { result: 'fail', reason: toReason(result.error) }),
    });
  }
};

/**
 * 인증을 마치고 복사가 이어지는 시점. 예전 `*_build_view`(빌드 화면 도착) 자리를 대신한다.
 *
 * 착지 경로에서만 부른다 — 로그인 사용자의 제자리 복사에는 인증 왕복이 없어 이 칸이 없다.
 * 그래서 이 이벤트 대비 `copy_complete` 비율이 곧 **인증 후 이탈률**이다.
 */
export const trackQuizCopyResume = (
  context: QuizCopyContext,
  sourceQuizsetId: string,
) => {
  if (context.copySource === 'guest_share' && context.shareFlowId) {
    trackEvent('quiz_share_guest_resume_view', {
      share_flow_id: context.shareFlowId,
      quiz_set_id: sourceQuizsetId,
    });
  }
  if (context.copySource === 'trial_gate' && context.trialSessionId) {
    trackEvent('quiz_play_trial_gate_resume_view', {
      trial_session_id: context.trialSessionId,
      quiz_set_id: sourceQuizsetId,
    });
  }
};

/**
 * 학생 계정이라 복사가 막혔다.
 *
 * 차단을 **판정한 쪽이 한 번** 부른다 — `/copy/[quizsetId]` 는 `useCopyFlow`,
 * `/duplicate/[id]` 는 `useTrackStudentBlockView`. 안내 화면(`CopyNoticeScreen`)은 문구만
 * 그리므로 계측하지 않는다.
 */
export const trackQuizCopyBlocked = (
  context: QuizCopyContext,
  sourceQuizsetId: string,
) => {
  if (context.copySource !== 'guest_share' || !context.shareFlowId) return;
  trackEvent('quiz_share_guest_block_view', {
    share_flow_id: context.shareFlowId,
    quiz_set_id: sourceQuizsetId,
  });
};
